import { Component, Input, OnDestroy, OnInit, signal, input, effect } from '@angular/core';
import { Observable } from 'rxjs';
import { Daypart, DaypartDay, Vehicle } from 'src/app/interfaces';
import { ucFirst } from 'src/app/globals';
import { dataStore, db } from 'src/app/app.component';
import {
  collection,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
} from 'firebase/firestore';
import { Unsubscribe } from 'firebase/auth';
import { Role } from 'src/app/enums';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatList, MatListItem } from '@angular/material/list';
import { RouterLink } from '@angular/router';
import { NgStyle, DatePipe } from '@angular/common';

@Component({
  selector: 'app-planning',
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss'],
  imports: [MatCard, MatCardContent, MatList, MatListItem, RouterLink, NgStyle, DatePipe],
})
export class PlanningComponent implements OnInit, OnDestroy {
  userId: string;

  ucFirst = ucFirst;
  Role = Role;

  driverDayparts = signal<any[]>([]);
  plannerDayparts = signal<any[]>([]);
  organisationId = dataStore.selectedOrganisation.id;
  loading = signal(true);

  daypartsUnsub: Unsubscribe;

  userIdInput = input<string>(undefined, { alias: 'getUserId' });
  type = input<string>(undefined);

  constructor() {
    effect(() => {
      this.userIdInput();
      this.type();
      this.getPlannedUsers();
    });
  }

  ngOnInit(): void {
    // Initial fetch handled by effect
  }

  ngOnDestroy(): void {
    this.daypartsUnsub?.();
  }

  async getPlannedUsers() {
    this.loading.set(true);
    if (this.daypartsUnsub) {
      this.daypartsUnsub();
    }
    const userIdFromAdmin = this.userIdInput() || null;
    this.userId = userIdFromAdmin ? userIdFromAdmin : dataStore.rootUser.id;
    const queryConstraints = [];
    const type = this.type();

    if (type == Role.Driver) {
      queryConstraints.push(where('driverId', '==', this.userId));
    } else if (type == Role.CoDriver) {
      queryConstraints.push(where('coDriverId', '==', this.userId));
    } else if (type == Role.Planner) {
      queryConstraints.push(where('plannerIds', 'array-contains', this.userId));
    } else {
      return;
    }
    queryConstraints.push(where('date', '>', new Date(new Date().setHours(0, 0, 0, 0))));
    queryConstraints.push(orderBy('date'));
    queryConstraints.push(limit(25));

    this.daypartsUnsub = onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/daypartDays`),
        ...queryConstraints,
      ),
      async (snapshot) => {
        let daypartsArray = [];

        let doneWithCount = 0;
        if (snapshot.docs.length === 0) {
          this.loading.set(false);
          if (type == Role.Driver || type == Role.CoDriver) {
            return this.driverDayparts.set([]);
          } else if (type == Role.Planner) {
            return this.plannerDayparts.set([]);
          }
        }

        for (const daypartDoc of snapshot.docs) {
          const userDaypart = { id: daypartDoc.id, ...daypartDoc.data() } as DaypartDay;
          const daypartDay = (
            await getDoc(
              doc(db, `organisations/${this.organisationId}/dayparts/${userDaypart.daypartId}`),
            )
          ).data() as Daypart;

          if (daypartDay) {
            // console.log('daypartDay', daypartDay);
            const daypart = daypartDay.daypart;
            const saveObj = {} as any;
            saveObj.date = userDaypart.date.toDate();
            saveObj.start = userDaypart.start.toDate();
            saveObj.end = userDaypart.end.toDate();
            saveObj.dayNumber = userDaypart.day;
            saveObj.monthNumber = userDaypart.month;
            saveObj.yearNumber = userDaypart.year;
            switch (daypart.day) {
              case 0:
                saveObj.day = 'Maandag';
                break;
              case 1:
                saveObj.day = 'Dinsdag';
                break;
              case 2:
                saveObj.day = 'Woensdag';
                break;
              case 3:
                saveObj.day = 'Donderdag';
                break;
              case 4:
                saveObj.day = 'Vrijdag';
                break;
              case 5:
                saveObj.day = 'Zaterdag';
                break;
              case 6:
                saveObj.day = 'Zondag';
                break;
            }

            if (type == Role.Driver || type == Role.CoDriver) {
              saveObj.vehicleId = userDaypart.vehicleId;
              if (userDaypart.vehicleId) {
                saveObj.vehicle = (
                  await getDoc(
                    doc(
                      db,
                      `organisations/${this.organisationId}/vehicles/${userDaypart.vehicleId}`,
                    ),
                  )
                ).data() as Vehicle;
              }
              saveObj.driverId = userDaypart.driverId;
              if (userDaypart.coDriverId) {
                saveObj.coDriverId = userDaypart.coDriverId;
              }
            }

            // format time
            saveObj.startHour = userDaypart.customStart
              ? `0${userDaypart.startHour}`.slice(-2)
              : `0${daypart.startHour}`.slice(-2);
            saveObj.startMin = userDaypart.customStart
              ? `0${userDaypart.startMinute}`.slice(-2)
              : `0${daypart.startMin}`.slice(-2);
            saveObj.endHour = userDaypart.customEnd
              ? `0${userDaypart.endHour}`.slice(-2)
              : `0${daypart.endHour}`.slice(-2);
            saveObj.endMin = userDaypart.customEnd
              ? `0${userDaypart.endMinute}`.slice(-2)
              : `0${daypart.endMin}`.slice(-2);

            saveObj.id = userDaypart.id;
            daypartsArray.push(saveObj);

            // console.log('doneWithCount', doneWithCount);
            // console.log('dayparts.length', dayparts.length);
          }
          doneWithCount++;
          // console.log('doneWithCount', doneWithCount);
          // console.log('dayparts.length', dayparts.length);
          if (doneWithCount === snapshot.docs.length) {
            const sortedDaypartsArray = daypartsArray.sort(function (a, b) {
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            });

            if (type == Role.Driver || type == Role.CoDriver) {
              this.driverDayparts.set(sortedDaypartsArray);
            } else if (type == Role.Planner) {
              this.plannerDayparts.set(sortedDaypartsArray);
            }
            this.loading.set(false);
          }
        }
      },
    );
  }

  compare(a, b) {
    if (a.startString < b.startString) {
      return -1;
    }
    if (a.startHour > b.startHour) {
      return 1;
    }
    return 0;
  }
}
